import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Feature } from '../../types/subscriptions';
import useFeatureGuard from './useFeatureGuard';
import useConfirm from '../useConfirm';
import useGetSubscription from './useGetSubscription';
import { ROUTES } from '../../constants/global';
import { FEATURE_PLAN_TYPE, PLANS } from '../../constants/subscriptions';

type PlanName = (typeof PLANS)[number]['name'];

function useSubscriptionUpsell(
  feature: Feature,
  {
    upsellOnMount = true,
  }: {
    upsellOnMount?: boolean;
  } = {},
) {
  const confirm = useConfirm();
  const navigate = useNavigate();

  const { data: subscriptionData } = useGetSubscription();
  const isAllowed = useFeatureGuard(feature);
  const requireUpsell = useMemo(
    () => !!subscriptionData?.isActive && !isAllowed,
    [subscriptionData?.isActive, isAllowed],
  );

  const upsell = useCallback(async () => {
    if (!requireUpsell) return;

    const title =
      feature === Feature.MANAGE_TEAM
        ? 'Add Artists to Your Shop'
        : feature === Feature.WAIVERS
        ? 'Access Waivers'
        : feature === Feature.MARKETING
        ? 'Send Email & SMS Campaigns'
        : '';
    const message =
      feature === Feature.MANAGE_TEAM
        ? "Porter's full-studio version is built to streamline your entire shop. Upgrade your subscription below to begin to add artists to your plan and start using Porter for your full studio!"
        : feature === Feature.WAIVERS
        ? "With Porter's waiver & consent form module, you can set up and automate your waiver process for appointments scheduled in Porter. Upgrade your subscription to begin using waivers through Porter!"
        : feature === Feature.MARKETING
        ? "With Porter's marketing module, you can easily re-target your customers with email & sms campaigns to get them back in the door! Upgrade your subscription to begin using marketing through Porter!"
        : '';

    if (!title || !message) return;

    const confirmed = await confirm({
      title,
      message,
      positiveText: 'View Plans',
      negativeText: 'Not Now',
      noWrapButtonsText: true,
    });

    if (!confirmed) return;

    const isStudioUpsell = FEATURE_PLAN_TYPE[feature] === 'STUDIO';

    const searchParams = new URLSearchParams();
    if (isStudioUpsell) {
      searchParams.set('plan', 'Studio Pro' as PlanName);
    } else {
      searchParams.set('plan', 'Artist Pro' as PlanName);
    }
    if (feature === Feature.MARKETING) {
      searchParams.set('redirect', ROUTES.marketing);
    } else if (feature === Feature.WAIVERS) {
      searchParams.set('redirect', ROUTES.waivers);
    } else if (feature === Feature.MANAGE_TEAM) {
      searchParams.set('redirect', ROUTES.team);
    }

    const stringifiedSearchParams = searchParams.toString();

    navigate(
      `${ROUTES.plans}${
        stringifiedSearchParams ? `?${stringifiedSearchParams}` : ''
      }`,
    );
  }, [requireUpsell, feature, confirm, navigate]);

  useEffect(() => {
    if (upsellOnMount && requireUpsell) {
      upsell();
    }
  }, [requireUpsell, upsell, upsellOnMount]);

  return {
    isSubscriptionActive: !!subscriptionData?.isActive,
    requireUpsell,
    upsell,
    hasFeature: isAllowed,
  };
}

export default useSubscriptionUpsell;
